import { useCallback, useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  ApiClient, HeizungAbsicherungszeile, HeizungAnnahme, HeizungAusdehnungszeile, HeizungBefund,
  HeizungBilanzzeile, HeizungErzeugerzeile, HeizungErzeugungAntwort, HeizungErzeugungModell, HeizungJahresgangwerte,
  HeizungKaskadenzeile, HeizungNormverweis, HeizungPruefzeile,
} from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe, Lastdauerlinie,
  LoadingState, StatusBadge, Vorbehalte, annahmenVorbehalt, befundrang, bestaetigung, formatiereZahl, herkunft,
  spalteFuer, type Befundrang, type Fachstatus, type Spalte, type Vorbehalt, type Vorbehaltsart,
} from '../muster';
import {
  GRENZE_INSTALLIERT, HERKUNFT, auslegungssatz, rechenstandErzeugung, zustandAbsicherung, zustandAusdehnung,
  zustandBilanz, zustandErzeuger, zustandFreigabe, zustandPruefung,
} from './heizung/erzeugung';

/**
 * Erzeugung und Absicherung – Heizung KG 420 (Paket TH-2).
 *
 * ═══ Was die Seite zeigt ══════════════════════════════════════════════════
 *
 * Was die Werkzeuge `heizung-erzeuger`, `heizung-ausdehnung` und
 * `heizung-absicherung-pruefen` geschrieben haben: die Erzeugerbilanz nach
 * DIN EN 12828 Abschn. 4.2 mit Kaskade, die Wärmeerzeuger (WT-Leistungen),
 * die Absicherung je Erzeuger, Ausdehnung und Druckhaltung je Gruppe und die
 * Absicherungsprüfung (J15). Dazu den Heizlast-Jahresgang des maßgebenden
 * Laufs als Lastdauerlinie. Die Seite rechnet nichts.
 *
 * ═══ Was sie NICHT tut ═════════════════════════════════════════════════════
 *
 * Sie entscheidet nichts. Die Erzeugerbilanz (für 8000 heute 55,7 %) steht
 * unter dem Vorbehalt HZK-29; Kälteerzeuger sind nach DIN EN 378 „nicht
 * prüfbar“ (HZK-31/HZK-40). Der Jahresgang ist eine Stundenbilanz mit
 * Klimadaten und KEIN Auslegungsfall – die Linie der installierten Leistung
 * sagt das wörtlich, und die Auslegungsgröße steht daneben (Auflage main).
 *
 * TX-STANDARD S1: PageHeader mit Rechenstand und Exportleiste, Vorbehalte,
 * höchstens sechs Kennzahlen, DataTable mit Gruppen und Summenfuß (TX3),
 * Fachstatus S1.5, Annahmenliste S1.6, Normbezug.
 */

interface Rahmen { api: ApiClient; projekt: { id: string } }

function Marke({ s }: { s: Fachstatus & { titel?: string } }) {
  return (
    <>
      <StatusBadge klein zustand={s.zustand} text={s.text} />
      {s.titel ? <span className="schwach"> {s.titel}</span> : null}
    </>
  );
}

const herkunftText = (h: string | null) => (h ? HERKUNFT[h] ?? h : '—');

const BILANZ: Spalte<HeizungBilanzzeile>[] = [
  { schluessel: 'bezug', titel: 'Ebene', immerSichtbar: true,
    wert: (b) => (b.ebene === 'gebaeude' ? 'Gebäude' : b.bezug) },
  { schluessel: 'lauf', titel: 'Heizlast-Lauf', art: 'zahl', nachkomma: 0, wert: (b) => b.heizlastLaufnummer },
  { schluessel: 'normheizlast', titel: 'Normheizlast', ...spalteFuer('leistung_kw'), wert: (b) => b.normheizlastKw },
  { schluessel: 'glz', titel: 'Gleichzeitigkeit', ...spalteFuer('anteil'), wert: (b) => b.gleichzeitigkeitProzent },
  { schluessel: 'glzHerkunft', titel: 'Herkunft Gleichzeitigkeit', wert: (b) => herkunftText(b.gleichzeitigkeitHerkunft) },
  { schluessel: 'tww', titel: 'Trinkwasser', ...spalteFuer('leistung_kw'), wert: (b) => b.trinkwasserKw },
  { schluessel: 'twwHerkunft', titel: 'Herkunft Trinkwasser', wert: (b) => herkunftText(b.trinkwasserHerkunft), versteckt: true },
  { schluessel: 'aufheiz', titel: 'Aufheizzuschlag', ...spalteFuer('leistung_kw'), wert: (b) => b.aufheizKw, versteckt: true },
  { schluessel: 'erforderlich', titel: 'erforderlich', ...spalteFuer('leistung_kw'), wert: (b) => b.erforderlichKw },
  { schluessel: 'installiert', titel: 'installiert', ...spalteFuer('leistung_kw'), wert: (b) => b.installiertKw },
  { schluessel: 'deckung', titel: 'Deckungsgrad', ...spalteFuer('anteil'), wert: (b) => b.deckungsgradProzent },
  { schluessel: 'reserve', titel: 'Reserve', ...spalteFuer('leistung_kw'), wert: (b) => b.reserveKw },
  { schluessel: 'bewertung', titel: 'Bewertung', art: 'status',
    wert: (b) => zustandBilanz(b.bewertung, b.vollstaendigkeit).text,
    inhalt: (b) => <Marke s={zustandBilanz(b.bewertung, b.vollstaendigkeit)} /> },
  { schluessel: 'vorbehalt', titel: 'Vorbehalt', wert: (b) => b.vorbehalt ?? '' },
];

const KASKADE: Spalte<HeizungKaskadenzeile>[] = [
  { schluessel: 'stufe', titel: 'Stufe', art: 'zahl', nachkomma: 0, immerSichtbar: true, wert: (k) => k.stufe },
  { schluessel: 'erzeuger', titel: 'Erzeuger', wert: (k) => k.bezeichnung ?? '' },
  { schluessel: 'stufeKw', titel: 'Leistung', ...spalteFuer('leistung_kw'), wert: (k) => k.stufeKw },
  { schluessel: 'kumuliert', titel: 'kumuliert', ...spalteFuer('leistung_kw'), wert: (k) => k.kumuliertKw },
  { schluessel: 'deckung', titel: 'Deckung', ...spalteFuer('anteil'), wert: (k) => k.deckungProzent },
  { schluessel: 'folge', titel: 'Folgeregel', wert: (k) => k.folgeregel.replace(/_/g, ' ') },
  { schluessel: 'herkunft', titel: 'Herkunft', art: 'status', wert: (k) => herkunft(k.herkunft).text,
    inhalt: (k) => <Marke s={herkunft(k.herkunft)} /> },
];

const ERZEUGER: Spalte<HeizungErzeugerzeile>[] = [
  { schluessel: 'erzeuger', titel: 'Erzeuger', immerSichtbar: true, wert: (e) => e.bezeichnung ?? '' },
  { schluessel: 'art', titel: 'Bauteilart', wert: (e) => e.bauteilart ?? '' },
  { schluessel: 'raum', titel: 'Raum', wert: (e) => e.raumnummer ?? '' },
  { schluessel: 'installiert', titel: 'installiert', ...spalteFuer('leistung_kw'), wert: (e) => e.installiertKw,
    fuss: { art: 'summe', schranke: true } },
  { schluessel: 'herkunft', titel: 'Herkunft Leistung', wert: (e) => herkunftText(e.leistungHerkunft) },
  { schluessel: 'listeKw', titel: 'Geräteliste', ...spalteFuer('leistung_kw'), wert: (e) => e.listeKw,
    fuss: { art: 'summe', schranke: true } },
  { schluessel: 'abweichung', titel: 'Abweichung', ...spalteFuer('leistung_kw'), wert: (e) => e.abweichungKw, versteckt: true },
  { schluessel: 'absicherung', titel: 'Absicherung', art: 'status',
    wert: (e) => (e.absicherungBewertung ? zustandAbsicherung(e.absicherungBewertung).text : '—'),
    inhalt: (e) => (e.absicherungBewertung ? <Marke s={zustandAbsicherung(e.absicherungBewertung)} /> : '—') },
  { schluessel: 'bewertung', titel: 'Bewertung', art: 'status', wert: (e) => zustandErzeuger(e.bewertung).text,
    inhalt: (e) => <Marke s={zustandErzeuger(e.bewertung)} /> },
  { schluessel: 'hinweis', titel: 'Hinweis', wert: (e) => e.hinweis ?? '', versteckt: true },
];

const dn = (d: number | null) => (d === null ? '—' : `DN ${d}`);

const ABSICHERUNG: Spalte<HeizungAbsicherungszeile>[] = [
  { schluessel: 'erzeuger', titel: 'Erzeuger', immerSichtbar: true, wert: (a) => a.bezeichnung ?? '' },
  { schluessel: 'gruppe', titel: 'Systemgruppe', wert: (a) => a.systemgruppe ?? '(ohne)' },
  { schluessel: 'art', titel: 'Bauteilart', wert: (a) => a.bauteilart },
  { schluessel: 'leistung', titel: 'Leistung', ...spalteFuer('leistung_kw'), wert: (a) => a.leistungKw },
  { schluessel: 'ansprech', titel: 'Ansprechdruck SV', ...spalteFuer('druck_bar'), wert: (a) => a.ansprechdruckBar },
  { schluessel: 'zulDruck', titel: 'zulässiger Druck', ...spalteFuer('druck_bar'), wert: (a) => a.zulaessigerDruckBar },
  { schluessel: 'zulTemp', titel: 'zulässige Temperatur', ...spalteFuer('temperatur'), wert: (a) => a.zulaessigeTemperaturC, versteckt: true },
  { schluessel: 'vorlauf', titel: 'Vorlauf', ...spalteFuer('temperatur'), wert: (a) => a.vorlaufC },
  { schluessel: 'sv', titel: 'SV Ein-/Austritt', wert: (a) => `${dn(a.svEintrittDn)} / ${dn(a.svAustrittDn)}` },
  { schluessel: 'bewertung', titel: 'Bewertung', art: 'status', wert: (a) => zustandAbsicherung(a.bewertung).text,
    inhalt: (a) => <Marke s={zustandAbsicherung(a.bewertung)} /> },
  { schluessel: 'hinweis', titel: 'Hinweis', wert: (a) => a.hinweis ?? '', versteckt: true },
];

const AUSDEHNUNG: Spalte<HeizungAusdehnungszeile>[] = [
  { schluessel: 'gruppe', titel: 'Systemgruppe', immerSichtbar: true, wert: (a) => a.systemgruppe },
  { schluessel: 'vorlauf', titel: 'Vorlauf', ...spalteFuer('temperatur'), wert: (a) => a.vorlaufC },
  { schluessel: 'inhalt', titel: 'Anlageninhalt', ...spalteFuer('inhalt_l'), wert: (a) => a.anlageninhaltL },
  { schluessel: 'leistung', titel: 'Leistung', ...spalteFuer('leistung_kw'), wert: (a) => a.leistungKw },
  { schluessel: 'ausdehnung', titel: 'Ausdehnungsvolumen', ...spalteFuer('inhalt_l'), wert: (a) => a.ausdehnungL },
  { schluessel: 'vordruck', titel: 'Vordruck', ...spalteFuer('druck_bar'), wert: (a) => a.vordruckBar },
  { schluessel: 'ansprech', titel: 'Ansprechdruck SV', ...spalteFuer('druck_bar'), wert: (a) => a.ansprechdruckBar },
  { schluessel: 'enddruck', titel: 'Enddruck', ...spalteFuer('druck_bar'), wert: (a) => a.enddruckBar },
  { schluessel: 'erf', titel: 'Nennvolumen erforderlich', ...spalteFuer('inhalt_l'), wert: (a) => a.nennvolumenErfL },
  { schluessel: 'modell', titel: 'Nennvolumen Modell', ...spalteFuer('inhalt_l'), wert: (a) => a.nennvolumenModellL },
  { schluessel: 'bewertung', titel: 'Bewertung', art: 'status', wert: (a) => zustandAusdehnung(a.bewertung).text,
    inhalt: (a) => <Marke s={zustandAusdehnung(a.bewertung)} /> },
  { schluessel: 'vdi', titel: 'VDI 2035', wert: (a) => a.vdi2035Stufe ?? '' },
  { schluessel: 'leitung', titel: 'Ausdehnungsleitung', wert: (a) => dn(a.leitungNennweiteDn) },
  { schluessel: 'hinweis', titel: 'Hinweis', wert: (a) => a.hinweis ?? '', versteckt: true },
];

const gegenstand = (p: HeizungPruefzeile) =>
  (p.ebene === 'erzeuger' && p.erzeuger ? `${p.systemgruppe} · ${p.erzeuger}` : p.systemgruppe);

const PRUEFUNG: Spalte<HeizungPruefzeile>[] = [
  { schluessel: 'gegenstand', titel: 'Gegenstand', immerSichtbar: true, wert: gegenstand },
  { schluessel: 'ergebnis', titel: 'Ergebnis', art: 'status', wert: (p) => zustandPruefung(p.stand, p.text).text,
    inhalt: (p) => <Marke s={zustandPruefung(p.stand, p.stand === 'entfaellt' ? p.text : '')} /> },
  { schluessel: 'ist', titel: 'Istwert', art: 'zahl', nachkomma: 2, wert: (p) => p.istwert },
  { schluessel: 'grenze', titel: 'Grenzwert', art: 'zahl', nachkomma: 2, wert: (p) => p.grenzwert },
  { schluessel: 'einheit', titel: 'Einheit', wert: (p) => p.einheit ?? '' },
  { schluessel: 'text', titel: 'Begründung', wert: (p) => p.text },
  { schluessel: 'norm', titel: 'Norm', wert: (p) => p.norm ?? '', versteckt: true },
  { schluessel: 'regel', titel: 'Regel bestätigt', art: 'status', wert: (p) => bestaetigung(p.regelBestaetigt).text,
    inhalt: (p) => <Marke s={bestaetigung(p.regelBestaetigt)} /> },
];

const RANG: Befundrang[] = ['F', 'W', 'H'];

const BEFUNDE: Spalte<HeizungBefund>[] = [
  { schluessel: 'rang', titel: 'Schwere', art: 'status', wert: (b) => b.rangText,
    inhalt: (b) => <Marke s={befundrang(RANG[b.rang] ?? 'H')} /> },
  { schluessel: 'regel', titel: 'Regel', wert: (b) => b.regel },
  { schluessel: 'gegenstand', titel: 'Gegenstand', wert: (b) => b.gegenstand },
  { schluessel: 'meldung', titel: 'Meldung', wert: (b) => b.meldung },
];

const ANNAHMEN: Spalte<HeizungAnnahme>[] = [
  { schluessel: 'parameter', titel: 'Parameter', immerSichtbar: true, wert: (a) => a.parameter },
  { schluessel: 'wert', titel: 'Wert', wert: (a) => a.wert ?? 'offen' },
  { schluessel: 'einheit', titel: 'Einheit', wert: (a) => a.einheit ?? '' },
  { schluessel: 'herkunft', titel: 'Herkunft', art: 'status', wert: (a) => herkunft(a.herkunft).text,
    inhalt: (a) => <Marke s={herkunft(a.herkunft)} /> },
  { schluessel: 'quelle', titel: 'Quelle/Norm', wert: (a) => [a.quelle, a.normbezug].filter(Boolean).join(' · ') },
  { schluessel: 'bestaetigt', titel: 'Bestätigung', art: 'status', wert: (a) => bestaetigung(a.bestaetigt).text,
    inhalt: (a) => <Marke s={bestaetigung(a.bestaetigt)} /> },
];

const NORMEN: Spalte<HeizungNormverweis>[] = [
  { schluessel: 'norm', titel: 'Norm', immerSichtbar: true, wert: (n) => n.norm },
  { schluessel: 'ausgabe', titel: 'Ausgabe', wert: (n) => n.ausgabe ?? '—' },
  { schluessel: 'abschnitt', titel: 'Abschnitt', wert: (n) => n.abschnitt ?? '—' },
  { schluessel: 'anwendung', titel: 'Anwendung', wert: (n) => n.anwendung },
];

export function HeizungErzeugung() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <HeizungErzeugungAnsicht api={api} projektId={projekt.id} />;
}

export function HeizungErzeugungAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [daten, setDaten] = useState<HeizungErzeugungAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      const antwort = await api.heizungErzeugung(projektId);
      setDaten(antwort ?? null);
      if (!antwort) setFehler('Die Antwort war leer.');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projektId]);

  useEffect(() => { void laden(); }, [laden]);

  const modell = daten?.modell ?? null;

  const kopf = (
    <PageHeader
      titel="Erzeugung und Absicherung"
      beschreibung={'Erzeugerbilanz und Kaskade, Wärmeerzeuger, Absicherung je Erzeuger, Ausdehnung und '
        + 'Absicherungsprüfung (J15) nach DIN EN 12828 – gelesen aus den Heizungswerkzeugen, nichts entschieden.'}
      meta={daten ? rechenstandErzeugung(daten) : undefined}
      aktionen={(
        <ExportLeiste ziele={[
          { art: 'pdf', laden: async () => undefined,
            gesperrt: { grund: 'Der Bericht HZG-DHA folgt mit TH-6 (Fachfragen HZK-01, HZK-02, HZK-29, HZK-30).' } },
          { art: 'excel', laden: () => api.heizungErzeugungExcel(projektId),
            gesperrt: modell ? undefined : { grund: 'Noch kein Lauf – ohne Bestand gibt es keine Liste.' } },
        ]} />
      )}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState titel="Erzeugung und Absicherung konnten nicht geladen werden." meldung={fehler}
                    beiErneutVersuchen={() => { void laden(); }} />
      </>
    );
  }

  if (!daten) return <>{kopf}<LoadingState text="Erzeugung und Absicherung werden geladen …" /></>;

  if (!modell) {
    return (
      <>
        {kopf}
        <EmptyState
          titel="Noch keine Erzeugerbilanz und keine Absicherungsprüfung gerechnet."
          erklaerung={'Die Seite liest, was die Werkzeuge „heizung-erzeuger“ (Erzeugerbilanz, Kaskade), '
            + '„heizung-ausdehnung“ (Ausdehnung, Absicherung) und „heizung-absicherung-pruefen“ (J15) '
            + 'schreiben. Ohne Rohrnetzlauf und maßgebenden Heizlastlauf gibt es nichts zu bilanzieren.'} />
        <p className="schwach"><Link to="../heizung-berechnung">Zum Rohrnetz</Link></p>
      </>
    );
  }

  const gebaeude = modell.bilanz.find((b) => b.ebene === 'gebaeude') ?? null;
  const offen = modell.annahmen.filter((a) => !a.bestaetigt).length;
  const nichtErfuellt = modell.pruefungen.filter((p) => p.stand === 'nicht_erfuellt').length;
  const nichtPruefbar = modell.pruefungen.filter((p) => p.stand === 'nicht_pruefbar').length;
  const ausdehnungGedeckt = modell.ausdehnung.filter((a) => a.bewertung === 'gedeckt').length;
  const deckung = gebaeude ? zustandBilanz(gebaeude.bewertung, gebaeude.vollstaendigkeit) : null;
  const freigabe = zustandFreigabe(modell.freigabestatus);

  return (
    <>
      {kopf}

      {/* Vorbehalte direkt unter dem Kopf (TX-STANDARD 1.6 S1.2 Nr. 2) im gemeinsamen Kasten (TX3):
          Annahmen, dann die offenen Fachfragen HZK-29/HZK-37/HZK-31 – Wortlaut aus dem Datenmodell,
          derselbe wie in der Excel-Übersicht. */}
      <Vorbehalte eintraege={vorbehaltseintraege(modell, offen)}
                  zusatz={<p className="schwach">Stand: <StatusBadge klein zustand={freigabe.zustand} text={freigabe.text} /></p>} />

      <KpiReihe>
        <KpiCard titel="erforderlich (Normheizlast + Trinkwasser)" wert={gebaeude?.erforderlichKw ?? null}
                 einheit="kW" nachkomma={1}
                 vergleich={gebaeude?.heizlastLaufnummer != null ? `Heizlast Lauf ${gebaeude.heizlastLaufnummer}` : undefined} />
        <KpiCard titel="installiert" wert={gebaeude?.installiertKw ?? null} einheit="kW" nachkomma={1}
                 vergleich={gebaeude ? `${gebaeude.erzeuger} Erzeuger, ${gebaeude.erzeugerOhneLeistung} ohne Leistung` : undefined} />
        <KpiCard titel="Deckungsgrad" wert={gebaeude?.deckungsgradProzent ?? null} einheit="%" nachkomma={1}
                 zustand={deckung?.zustand} zustandText={deckung?.text}
                 fussnote="Vorbehalt HZK-29 – nicht entschieden" />
        <KpiCard titel="Absicherungsprüfung J15 nicht erfüllt" wert={nichtErfuellt}
                 zustand={nichtErfuellt > 0 ? 'fehler' : 'gut'}
                 zustandText={nichtErfuellt > 0 ? 'nicht erfüllt' : 'keine'}
                 vergleich={`${nichtPruefbar} nicht prüfbar von ${modell.pruefungen.length}`} />
        <KpiCard titel="Ausdehnung gedeckt" wert={ausdehnungGedeckt}
                 vergleich={`von ${modell.ausdehnung.length} Systemgruppen`} />
      </KpiReihe>

      <ContentSection titel="Erzeugerbilanz (DIN EN 12828, Abschn. 4.2)"
        beschreibung={'Die Kette Glied für Glied: Normheizlast × Gleichzeitigkeit + Trinkwasser + Aufheizzuschlag '
          + '= erforderlich, gegen die installierte Leistung. Gruppenbilanzen sind untere Schranken, solange '
          + 'Verbraucher ohne Weg sind.'}>
        <DataTable name="Erzeugerbilanz" spalten={BILANZ} zeilen={modell.bilanz}
                   schluessel={(b) => `${b.ebene}-${b.bezug}`} suche={false} dicht
                   leer={<EmptyState klein titel="Keine Erzeugerbilanz."
                     erklaerung="Das Werkzeug „heizung-erzeuger“ ist für dieses Projekt noch nicht gelaufen." />} />
        <h3>Kaskade</h3>
        <DataTable name="Kaskade" spalten={KASKADE} zeilen={modell.kaskade}
                   schluessel={(k) => `${k.bezug}-${k.stufe}`} suche={false} dicht
                   leer={<EmptyState klein titel="Keine Kaskade." erklaerung="Ohne Erzeuger mit Leistung keine Stufen." />} />
        <p className="schwach">Die Folgeregel „größte zuerst“ ist eine Annahme (Vorgabe erz_kaskade_folgeregel); im
          Modell steht keine Folgeschaltung.</p>
      </ContentSection>

      <ContentSection titel="Wärmeerzeuger (WT-Leistungen)"
        beschreibung={'Je Systemgruppe die Leistung am Bauteil und die Geräteliste des Fachplaners nebeneinander; '
          + 'keine überschreibt die andere. Summen ohne Wert sind untere Schranken.'}>
        <DataTable name="Wärmeerzeuger" spalten={ERZEUGER} zeilen={modell.erzeuger}
                   schluessel={(e) => `${e.systemgruppe ?? '-'}-${e.bezeichnung ?? '-'}-${e.listeKennung ?? '-'}-${modell.erzeuger.indexOf(e)}`}
                   suche={false} dicht
                   gruppierung={{ nach: (e) => e.systemgruppe ?? '(ohne Systemgruppe)', titel: (s) => `Systemgruppe ${s}` }}
                   ausfuhr={modell.erzeuger.length > 20} ausfuhrDateiname="heizung-waermeerzeuger.csv"
                   leer={<EmptyState klein titel="Keine Wärmeerzeuger."
                     erklaerung="Im Planungsmodell ist kein Erzeuger erkannt." />} />
      </ContentSection>

      <JahresgangAbschnitt api={api} projektId={projektId} gebaeude={gebaeude} />

      <ContentSection titel="Absicherung je Erzeuger (DIN EN 12828, Abschn. 4.6)"
        beschreibung={'Sicherheitsventil je Wärmeerzeuger: Ansprechdruck, Abblaseleistung bis 900 kW aus der '
          + 'Tabelle, darüber Einzelnachweis. Der Ansatz 3,0 bar ist unbestätigt (HZK-01).'}>
        <DataTable name="Absicherung" spalten={ABSICHERUNG} zeilen={modell.absicherung}
                   schluessel={(a) => `${a.systemgruppe ?? '-'}-${a.bezeichnung ?? '-'}-${modell.absicherung.indexOf(a)}`}
                   suche={modell.absicherung.length > 20} dicht
                   ausfuhr={modell.absicherung.length > 20} ausfuhrDateiname="heizung-absicherung.csv"
                   leer={<EmptyState klein titel="Keine Absicherung gerechnet."
                     erklaerung="Das Werkzeug „heizung-ausdehnung“ schreibt die Absicherung je Erzeuger." />} />
      </ContentSection>

      <ContentSection titel="Ausdehnung und Druckhaltung je Systemgruppe"
        beschreibung={'DIN EN 12828 Anh. D, VDI 4708-1, VDI 2035-1: Anlageninhalt, Ausdehnung, Vordruck, Enddruck, '
          + 'erforderliches Nennvolumen gegen das Gefäß im Modell; dazu die Ausdehnungsleitung.'}>
        <DataTable name="Ausdehnung" spalten={AUSDEHNUNG} zeilen={modell.ausdehnung}
                   schluessel={(a) => a.systemgruppe} suche={false} dicht
                   leer={<EmptyState klein titel="Keine Ausdehnung gerechnet."
                     erklaerung="Das Werkzeug „heizung-ausdehnung“ ist noch nicht gelaufen." />} />
      </ContentSection>

      <ContentSection titel="Absicherungsprüfung (J15)"
        beschreibung={'Jede Regel gegen jede Gruppe bzw. jeden Erzeuger: erfüllt, nicht erfüllt, nicht prüfbar '
          + '(Eingabe fehlt) oder entfällt (mit Grund). Die Regeln sind unbestätigt; Normwerte prüfen.'}>
        <DataTable name="Absicherungsprüfung" spalten={PRUEFUNG} zeilen={modell.pruefungen}
                   schluessel={(p) => `${p.regel}-${gegenstand(p)}-${modell.pruefungen.indexOf(p)}`}
                   suche dicht
                   gruppierung={{
                     nach: (p) => p.regelbezeichnung,
                     zusatz: (_s, zeilen) => {
                       const ne = zeilen.filter((z) => z.stand === 'nicht_erfuellt').length;
                       const np = zeilen.filter((z) => z.stand === 'nicht_pruefbar').length;
                       return (
                         <>
                           {ne > 0 && <StatusBadge klein zustand="fehler" text={`${ne} nicht erfüllt`} />}
                           {np > 0 && <StatusBadge klein zustand="warnung" text={`${np} nicht prüfbar`} />}
                         </>
                       );
                     },
                     zugeklappt: 'alle',
                   }}
                   ausfuhr={modell.pruefungen.length > 20} ausfuhrDateiname="heizung-absicherungspruefung.csv"
                   leer={<EmptyState klein titel="Keine Absicherungsprüfung."
                     erklaerung="Das Werkzeug „heizung-absicherung-pruefen“ ist noch nicht gelaufen." />} />
      </ContentSection>

      <ContentSection titel="Befunde" beschreibung="Was nicht erfüllt oder nicht prüfbar ist – Fehler zuerst.">
        <DataTable name="Befunde" spalten={BEFUNDE} zeilen={modell.befunde}
                   schluessel={(b) => `${b.rang}-${b.regel}-${b.gegenstand}-${b.meldung}`} suche={false} dicht
                   ausfuhr={modell.befunde.length > 20} ausfuhrDateiname="heizung-erzeugung-befunde.csv"
                   leer={<EmptyState klein titel="Keine Befunde." erklaerung="Kein Ergebnis „nicht erfüllt“ oder „nicht prüfbar“." />} />
      </ContentSection>

      <ContentSection id="erzeugung-annahmen" titel="Annahmen und Vorgaben"
        beschreibung={'Jede genutzte Vorgabe (Erzeuger, Ausdehnung) und jede Regel der Absicherungsprüfung – '
          + 'dieselbe Liste wie im Blatt „Annahmen“ der Excel-Mappe. Unbestätigte stehen oben.'}>
        <DataTable name="Annahmen und Vorgaben" spalten={ANNAHMEN} zeilen={modell.annahmen}
                   schluessel={(a) => a.parameter} suche={modell.annahmen.length > 20} dicht
                   ausfuhr={modell.annahmen.length > 20} ausfuhrDateiname="heizung-erzeugung-annahmen.csv"
                   leer={<EmptyState klein titel="Keine Annahmen genutzt." />} />
      </ContentSection>

      <ContentSection titel="Grundlagen und Normbezug">
        <DataTable name="Normbezug" spalten={NORMEN} zeilen={modell.normbezug}
                   schluessel={(n) => `${n.norm}-${n.abschnitt ?? ''}`} suche={false} dicht
                   leer={<EmptyState klein titel="Kein Normbezug." />} />
        <p className="schwach">
          Rohrnetz, Heizflächen und Dämmung stehen auf der Seite <Link to="../heizung-berechnung">„Rohrnetz“</Link>,
          die Kälteerzeugung nach DIN EN 378 auf der Seite <Link to="../kaelte-nachweis">„Kältenachweis (EN 378)“</Link>.
        </p>
      </ContentSection>
    </>
  );
}

/** Die Einträge des Vorbehaltskastens: Annahmen (TX-Satz) und die Vorbehalte des Datenmodells. */
export function vorbehaltseintraege(modell: HeizungErzeugungModell, offen: number): Vorbehalt[] {
  const liste: Vorbehalt[] = modell.vorbehalte.map((v) => ({
    art: v.art as Vorbehaltsart, text: v.text, verweis: v.verweis ?? undefined,
  }));
  if (offen > 0) liste.unshift(annahmenVorbehalt(offen, modell.annahmen.length, 'Annahmen und Vorgaben', '#erzeugung-annahmen'));
  return liste;
}

/**
 * Die Lastdauerlinie des Heizlast-Jahresgangs – eigener Abruf (8.760 Werte),
 * eigene Zustände. Auflage main zu TH-2: Grundlage benannt, KEIN
 * Auslegungsfall; die Auslegungsgröße (Erzeugerbilanz) steht daneben; die
 * Linie der installierten Leistung trägt genau {@link GRENZE_INSTALLIERT}.
 */
function JahresgangAbschnitt({ api, projektId, gebaeude }: {
  api: ApiClient; projektId: string; gebaeude: HeizungBilanzzeile | null;
}) {
  const [werte, setWerte] = useState<HeizungJahresgangwerte | null | undefined>(undefined);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(async () => {
    setFehler(null);
    setWerte(undefined);
    try {
      setWerte((await api.heizungErzeugungJahresgang(projektId)) ?? null);
    } catch (e) {
      setFehler(e);
    }
  }, [api, projektId]);

  useEffect(() => { void laden(); }, [laden]);

  const installiert = gebaeude?.installiertKw ?? null;

  return (
    <ContentSection titel="Heizlast im Jahresverlauf (Lastdauerlinie)"
      beschreibung={werte?.grundlage ?? 'Stundenbilanz des Heizwärmebedarfs zum maßgebenden Heizlastlauf – kein Auslegungsfall.'}>
      <p className="hinweis" role="note" data-auslegung="">
        {auslegungssatz(gebaeude?.erforderlichKw ?? null, installiert, gebaeude?.deckungsgradProzent ?? null,
          (x, n) => formatiereZahl(x, n))}
      </p>
      <Lastdauerlinie
        titel={werte ? `Heizwärmebedarf je Stunde, Heizlast Lauf ${werte.laufnummer}` : 'Heizwärmebedarf je Stunde'}
        einheit="kW" achsenTitel="Heizwärmebedarf" nachkomma={1}
        werte={werte?.bedarfKw ?? []}
        zeitpunkt={(i) => werte?.zeitpunkte[i] ?? String(i + 1)}
        schwellen={installiert !== null ? [installiert] : []}
        grenzen={installiert !== null ? [{ wert: installiert, titel: GRENZE_INSTALLIERT, zustand: 'info' }] : []}
        laedt={werte === undefined && !fehler}
        fehler={fehler ?? undefined}
        beiErneutVersuchen={() => { void laden(); }}
        leer={<EmptyState klein titel="Kein Jahresgang zum maßgebenden Heizlastlauf."
          erklaerung="Die Jahressimulation (Bahn TL) ist für diesen Lauf nicht gerechnet, oder es gibt keinen maßgebenden Heizlastlauf." />}
      />
    </ContentSection>
  );
}
